import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { SwalService } from '@shared/utils/swal.service';
import { ArticuloService } from '../services/articulo.service';
import { ILineaArticulo } from '../interfaces/iArticulo';
import { MatDialog } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';

@Component({
  selector: 'app-abc-articulo-form',
  templateUrl: './abc-articulo-form.component.html',
})
export class AbcArticuloFormComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() model: any = null;
  
  loading: boolean = false;
  modelRecived: any = null;
  listLineas: ILineaArticulo[] = [];

  form = this._fb.group({
    numero_articulo: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
    numero_linea: ['', [Validators.required]],
    descripcion: ['', [Validators.required]],
    precio: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
    precio_Propio: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
    fecha_Cotizacion: ['', [Validators.required]]
  });

  constructor(
    private _fb: UntypedFormBuilder,
    private articuloService: ArticuloService,
    private _swalService: SwalService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadLineas();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model'] && changes['model'].currentValue) {
      this.setForm();
    } else if (changes['visible'] && changes['visible'].currentValue) {
      if (!this.model) {
        this.form.reset();
        this.form.controls["numero_articulo"].enable();
        this.form.controls["precio_Propio"].enable();
      }
    }
  }

  loadLineas() {
    this.loading = true;
    this.articuloService.GetLineasArticulos().subscribe({
      next: (res) => {
        if (res.success) {
          this.listLineas = res.data;
        } else {
          this._swalService.error('Error al cargar las líneas');
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error al cargar líneas:', err);
        this._swalService.error('Error al cargar las líneas');
        this.loading = false;
      }
    });
  }

  formValue(value: string) {
    return this.form.get(value);
  }

  setForm(): void {
    if (this.model) {
      this.loading = true;
      this.articuloService.GetArticulo(this.model.numero_articulo).subscribe({
        next: (res) => {
          if (res.success) {
            this.articuloService.getFechaString(res.data.fecha_Cotizacion).subscribe({
              next: (fechaResponse) => {
                if (fechaResponse.success && fechaResponse.data.sFecha) {
                  // Convertir el string de fecha (dd/mm/yyyy) a formato YYYY-MM-DD para el input type="date"
                  const [day, month, year] = fechaResponse.data.sFecha.split('/');
                  const fechaFormateada = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                  
                  this.modelRecived = { ...res.data };
                  this.form.patchValue({
                    numero_articulo: res.data.numero_articulo,
                    numero_linea: res.data.numero_linea,
                    descripcion: res.data.descripcion,
                    precio: res.data.precio,
                    precio_Propio: res.data.precio_Propio,
                    fecha_Cotizacion: fechaFormateada
                  });
                }
                this.form.controls["numero_articulo"].disable();
                this.form.controls["precio_Propio"].disable();
                this.loading = false;
              },
              error: (err) => {
                console.error('Error al convertir fecha:', err);
                this._swalService.error('Error al cargar la fecha');
                this.loading = false;
              }
            });
          } else {
            this._swalService.error('Error al cargar el artículo');
            this.loading = false;
          }
        },
        error: (err) => {
          console.error('Error al cargar artículo:', err);
          this._swalService.error('Error al cargar el artículo');
          this.loading = false;
        }
      });
    } else {
      this.form.reset();
      this.form.controls["numero_articulo"].enable();
      this.form.controls["precio_Propio"].enable();
    }
  }

  create(model: any) {
    if (this.form.valid) {
      this.loading = true;
      const articulo = {
        ...this.form.value,
        fecha_Cotizacion: this.form.value.fecha_Cotizacion // Ya viene en formato YYYY-MM-DD
      };

      this.articuloService.CreateArticulo(articulo).subscribe({
        next: (res) => {
          if (res.success) {
            this._swalService.success('Artículo creado exitosamente');
            this.close(true);
          } else {
            this._swalService.error('Error al crear el artículo');
          }
          this.loading = false;
        },
        error: (err) => {
          console.error('Error al crear artículo:', err);
          this._swalService.error('Error al crear el artículo');
          this.loading = false;
        }
      });
    } else {
      this._swalService.warning('Por favor complete todos los campos requeridos');
    }
  }

  update(model: any) {
    if (this.form.valid) {
      this.loading = true;
      const articulo = {
        ...this.form.value,
        numero_articulo: this.modelRecived.numero_articulo,
        fecha_Cotizacion: this.form.value.fecha_Cotizacion // Ya viene en formato YYYY-MM-DD
      };

      this.articuloService.UpdateArticulo(articulo).subscribe({
        next: (res) => {
          if (res.success) {
            this._swalService.success('Artículo actualizado exitosamente');
            this.close(true);
          } else {
            this._swalService.error('Error al actualizar el artículo');
          }
          this.loading = false;
        },
        error: (err) => {
          console.error('Error al actualizar artículo:', err);
          this._swalService.error('Error al actualizar el artículo');
          this.loading = false;
        }
      });
    } else {
      this._swalService.warning('Por favor complete todos los campos requeridos');
    }
  }

  close(refresh: boolean = false) {
    this.visible = false;
    this.visibleChange.emit(refresh);
  }

  customizables(): void {
    const dialogRef = this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: "1000px",
      height: "1000px",
      disableClose: true,
      panelClass: ["custom-dialog-zindex"],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: "articulos",
        sClave: `${this.modelRecived.numero_articulo}`,
        sNombre: this.modelRecived.descripcion,
        sTipo: "Opcion",
      },
    });
  }
} 